import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SquarePayment from '../components/SquarePayment';
import BookingFlow from '../components/BookingFlow';
import { TEST_CARDS } from '../config/squareConfig';

const SquareDemo = () => {
  const navigate = useNavigate();
  const [demoMode, setDemoMode] = useState('payment'); // 'payment', 'booking', 'flow'
  const [demoBookingData] = useState({
    booking_ref_id: 'DEMO-' + Date.now(),
    passenger_id: 'demo-passenger-123',
    final_fare: '25.50',
    payment_type: 'FULL'
  });

  const handleDemoPaymentSuccess = (result) => {
    alert(`Demo Payment Successful!\nTransaction ID: ${result.transaction_id || 'DEMO-TX-' + Date.now()}\nAmount: $${demoBookingData.final_fare}`);
  };

  const handleDemoPaymentError = (error) => {
    alert(`Demo Payment Failed: ${error}`);
  };

  const handleDemoBookingComplete = (result) => {
    alert(`Demo Booking Complete!\nBooking ID: ${result.bookingRefId}\nPayment Status: Success`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Square Payment Integration Demo</h1>
            <p className="text-gray-600 mb-4">
              This demo showcases the Square payment integration with the booking system. 
              Use the test cards below in sandbox mode.
            </p>
            
            {/* Test Cards Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-3">Test Cards (Sandbox Mode)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Visa (Success):</strong> {TEST_CARDS.visa}</p>
                  <p><strong>Mastercard (Success):</strong> {TEST_CARDS.mastercard}</p>
                  <p><strong>Amex (Success):</strong> {TEST_CARDS.amex}</p>
                </div>
                <div>
                  <p><strong>Declined Card:</strong> {TEST_CARDS.declined}</p>
                  <p><strong>Insufficient Funds:</strong> {TEST_CARDS.insufficientFunds}</p>
                  <p><strong>Invalid Card:</strong> {TEST_CARDS.invalid}</p>
                </div>
              </div>
              <p className="text-blue-600 text-xs mt-2">
                Use any future expiry date and any 3-4 digit CVV
              </p>
            </div>

            {/* Demo Mode Selector */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setDemoMode('payment')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  demoMode === 'payment'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Payment Only
              </button>
              <button
                onClick={() => setDemoMode('flow')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  demoMode === 'flow'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Full Booking Flow
              </button>
            </div>
          </div>
        </div>

        {/* Demo Content */}
        {demoMode === 'payment' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Square Payment Component Demo</h2>
              <p className="text-gray-600 mb-4">
                This demonstrates the standalone Square payment component with a demo booking.
              </p>
              
              {/* Demo Booking Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-700 mb-2">Demo Booking Details</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Booking ID:</strong> {demoBookingData.booking_ref_id}</p>
                  <p><strong>Passenger ID:</strong> {demoBookingData.passenger_id}</p>
                  <p><strong>Amount:</strong> ${demoBookingData.final_fare}</p>
                  <p><strong>Payment Type:</strong> {demoBookingData.payment_type}</p>
                </div>
              </div>
            </div>

            <SquarePayment
              bookingRefId={demoBookingData.booking_ref_id}
              amount={parseFloat(demoBookingData.final_fare)}
              passengerId={demoBookingData.passenger_id}
              paymentType={demoBookingData.payment_type}
              onSuccess={handleDemoPaymentSuccess}
              onError={handleDemoPaymentError}
            />
          </div>
        )}

        {demoMode === 'flow' && (
          <div>
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Full Booking Flow Demo</h2>
              <p className="text-gray-600 mb-4">
                This demonstrates the complete booking flow including passenger registration, 
                booking creation, and Square payment processing.
              </p>
            </div>

            <BookingFlow
              onBookingComplete={handleDemoBookingComplete}
              onError={(error) => alert(`Demo Error: ${error}`)}
            />
          </div>
        )}

        {/* Integration Guide */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Integration Guide</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">1. Configuration</h3>
                <p className="text-gray-600 mb-2">Update your Square credentials in <code className="bg-gray-100 px-2 py-1 rounded">src/config/squareConfig.js</code>:</p>
                <pre className="bg-gray-100 p-4 rounded-md text-sm overflow-x-auto">
{`export const SQUARE_CONFIG = {
  applicationId: 'your-square-application-id',
  locationId: 'your-square-location-id',
  environment: 'sandbox', // or 'production'
};`}
                </pre>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">2. Basic Usage</h3>
                <p className="text-gray-600 mb-2">Import and use the SquarePayment component:</p>
                <pre className="bg-gray-100 p-4 rounded-md text-sm overflow-x-auto">
{`import SquarePayment from './components/SquarePayment';

<SquarePayment
  bookingRefId="your-booking-id"
  amount={25.50}
  passengerId="passenger-id"
  paymentType="FULL"
  onSuccess={(result) => console.log('Success:', result)}
  onError={(error) => console.log('Error:', error)}
/>`}
                </pre>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">3. Full Booking Flow</h3>
                <p className="text-gray-600 mb-2">Use the BookingFlow component for complete booking process:</p>
                <pre className="bg-gray-100 p-4 rounded-md text-sm overflow-x-auto">
{`import BookingFlow from './components/BookingFlow';

<BookingFlow
  onBookingComplete={(result) => {
    // Handle successful booking and payment
    console.log('Booking complete:', result);
  }}
  onError={(error) => {
    // Handle errors
    console.error('Booking error:', error);
  }}
/>`}
                </pre>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">4. Backend API Endpoints</h3>
                <p className="text-gray-600 mb-2">Ensure these endpoints are available on your backend:</p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li><code>POST /quote_api/register_passenger</code> - Register passenger</li>
                  <li><code>POST /quote_api/create_booking</code> - Create booking</li>
                  <li><code>POST /booking/ajax_make_payment</code> - Process Square payment</li>
                  <li><code>GET /quote_api/get_creditcards/&#123;passenger_id&#125;</code> - Get saved cards</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="max-w-4xl mx-auto mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
          >
            Back to Main App
          </button>
        </div>
      </div>
    </div>
  );
};

export default SquareDemo;
